import React, { useState, useEffect, useRef } from 'react';
import { withRouter, useHistory } from 'react-router-dom';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Form as FinalForm, FormSpy } from 'react-final-form';

import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min';
import {
  fetchGoogleCalendarAvailability,
} from '../../util/api';

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

// Utils
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { types as sdkTypes } from '../../util/sdkLoader';
import { formatMoney } from '../../util/currency';
import { calculateDiscount } from '../../util/discount';
import { getTitleAfterHyphen, truncateAtWord } from '../../util/string';
import { ensureListing } from '../../util/data';
import { createSlug } from '../../util/urlHelpers';
import {
  timestampToDate,
  getStartOf,
  timeOfDayFromLocalToTimeZone,
  stringifyDateToISO8601,
  isSameDay,
} from '../../util/dates';
import { timeSlotsPerDate } from '../../util/generators';
import { getMonthlyFetchRange } from '../../components/OrderPanel/booking.shared';
import { findRouteByRouteName, createResourceLocatorString } from '../../util/routes';
import { isSignupEmailTakenError } from '../../util/errors';
import {
  bookingResumeMatchesUser,
  clearBookingResumeDraft,
  getBookingResumeDraft,
  restoreBookingFlowSession,
  saveBookingResumeDraft,
} from '../../util/bookingResume';
import * as validators from '../../util/validators';
import getCountryCodes from '../../translations/countryCodes';


// Global ducks
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { authenticationInProgress, login, signup } from '../../ducks/auth.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { recoverPassword } from '../PasswordRecoveryPage/PasswordRecoveryPage.duck';

// Shared components
import {
  Page,
  LayoutSingleColumn,
  PrimaryButton,
  FieldSelect,
  Form,
  FieldTextInput,
  FieldPhoneNumberInput,
  FieldSingleDatePicker,
} from '../../components';

// Related components
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

// Page-specific imports
import {
  fetchTimeSlots,
  fetchTransactionLineItems,
  setInitialValues,
} from '../ListingPage/ListingPage.duck';
import { LoadingPage, ErrorPage } from '../ListingPage/ListingPage.shared';
import FieldDateAndTimeInputHourly from '../../components/OrderPanel/BookingTimeForm/FieldDateAndTimeInput';
import FieldDateAndTimeInputFixed from '../../components/OrderPanel/BookingFixedDurationForm/FieldDateAndTimeInput';
import { getAllTimeSlots } from '../../components/OrderPanel/booking.shared';
import FieldGuestCountPicker from './FieldGuestCountPicker';
import FieldCustomCountryPicker from '../../components/FieldCustomCountryPicker/FieldCustomCountryPicker';

import css from './ReservationSummaryPage.module.css';
import IconCard from '../../components/SavedCardDetails/IconCard/IconCard.js';
import BookingLoginModal from './BookingLoginModal';
import {
  MIXPANEL_EVENTS,
  listingMixpanelProperties,
  trackMixpanelEvent,
} from '../../util/mixpanel';

const { UUID, Money } = sdkTypes;

import {
  optimizeCloudinaryUrl,
  getCloudinarySrcSet,
  DESKTOP_HERO_SRCSET_WIDTHS,
  TABLET_SRCSET_WIDTHS,
  MOBILE_SRCSET_WIDTHS,
} from '../../util/cloudinaryHelpers';

const reservationContactStorageKey = listingId =>
  listingId ? `lamh.reservationContact.${listingId}` : 'lamh.reservationContact';

const getReservationContactSession = listingId => {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const raw = window.sessionStorage.getItem(reservationContactStorageKey(listingId));
    return raw ? JSON.parse(raw) : {};
  } catch (error) {
    return {};
  }
};

const saveReservationContactSession = (listingId, data) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const previous = getReservationContactSession(listingId);
    window.sessionStorage.setItem(
      reservationContactStorageKey(listingId),
      JSON.stringify({ ...previous, ...data })
    );
  } catch (error) {
    // Session storage can be unavailable in restricted browser modes.
  }
};

// Safe date parsing helper to handle Dates, timestamps (strings/numbers), or ISO strings
const parseDate = val => {
  if (val instanceof Date) {
    return val;
  }
  if (typeof val === 'number') {
    return new Date(val);
  }
  if (typeof val === 'string') {
    if (/^\d+$/.test(val)) {
      return new Date(Number.parseInt(val, 10));
    }
    const d = new Date(val);
    if (!isNaN(d.getTime())) {
      return d;
    }
  }
  return null;
};

export const getBookedOptionsForDate = (bookedData, dateStr) => {
  if (!bookedData || !dateStr) return [];

  // Object format: { "2026-08-01": ["TEST ONE"] }
  if (typeof bookedData === 'object' && bookedData !== null && !Array.isArray(bookedData)) {
    const val = bookedData[dateStr];
    if (val) {
      return Array.isArray(val) ? val.map(v => (typeof v === 'string' ? v.trim() : v)) : [val];
    }
    return [];
  }

  // Array of objects format: [{ date: "2026-08-01", optionName: "TEST ONE" }]
  if (Array.isArray(bookedData)) {
    return bookedData
      .map(item => {
        if (typeof item === 'object' && item !== null && item.date === dateStr) {
          return item.optionName || item.name;
        }
        return null;
      })
      .filter(Boolean);
  }

  return [];
};

/**
 * ReservationDetailsPage - Shows listing details and guest selection
 * This page is part of the booking flow and displays information similar to OrderPanel
 * but as a standalone page without using a modal.
 *
 * @component
 */
const ReservationDetailsPageComponent = props => {
  const {
    currentUser,
    getListing,
    getOwnListing,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    onManageDisableScrolling,
    onFetchTimeSlots,
    onFetchTransactionLineItems,
    monthlyTimeSlots,
    timeSlotsForDate,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    callSetInitialValues,
    onInitializeCardPaymentData,
    onUpdateProfile,
    onSignup,
    onLogin,
    onRecoverPassword,
    onFetchCurrentUser,
    loginInProgress,
    loginError,
    recoveryInProgress,
    recoveryError,
    bookingFlowData,
  } = props;

  const listingId = new UUID(rawParams.id);
  const currentListing = ensureListing(getListing(listingId));

  // Determine if this is a conversation flow or reservation flow
  const publicData = currentListing?.attributes?.publicData || {};
  const storedBookingType =
    location.state?.bookingData?.bookingType ||
    location.state?.bookingType ||
    bookingFlowData?.bookingType ||
    publicData?.bookingType ||
    'reservation';
  const isConversation = storedBookingType === 'conversation';

  const [selectedGuests, setSelectedGuests] = useState(isConversation ? '1' : '');
  const [seatsOptions, setSeatsOptions] = useState([1, 2, 3, 4, 5]);
  const [mounted, setMounted] = useState(false);
  const [accountableData, setAccountableDataState] = useState(null);
  const accountableDataRef = useRef(null);
  const setAccountableData = data => {
    setAccountableDataState(data);
    accountableDataRef.current = data;
  };
  const storedSession = getReservationContactSession(rawParams.id);
  const [bookingData, setBookingDataState] = useState(
    () => location.state?.bookingData || bookingFlowData?.bookingData || storedSession?.bookingData || null
  );

  const setBookingData = data => {
    setBookingDataState(data);
    saveReservationContactSession(rawParams.id, { bookingData: data });
  };

  const [guestData, setGuestDataState] = useState(
    () => location.state?.guestDetails || bookingFlowData?.guestDetails || storedSession?.guestDetails || null
  );

  const setGuestData = data => {
    setGuestDataState(data);
    saveReservationContactSession(rawParams.id, { guestDetails: data });
  };

  useEffect(() => {
    const currentSession = getReservationContactSession(rawParams.id);
    const activeBookingData =
      location.state?.bookingData || bookingFlowData?.bookingData || currentSession?.bookingData || null;
    const activeGuestData =
      location.state?.guestDetails || bookingFlowData?.guestDetails || currentSession?.guestDetails || null;

    if (activeBookingData) {
      setBookingDataState(activeBookingData);
    }
    if (activeGuestData) {
      setGuestDataState(activeGuestData);
    }
    if (activeBookingData || activeGuestData) {
      setShowReservationSummaryState(true);
      setShowDetailsFormState(false);
    }
  }, [location.state, bookingFlowData, rawParams.id]);

  const [showReservationSummary, setShowReservationSummaryState] = useState(true);

  const [showDetailsForm, setShowDetailsFormState] = useState(false);

  const setShowDetailsForm = val => {
    setShowDetailsFormState(val);
  };

  const setShowReservationSummary = val => {
    setShowReservationSummaryState(val);
  };
  const [submittingDetails, setSubmittingDetails] = useState(false);
  const [signupError, setSignupError] = useState(null);
  const [bookingCapacity, setBookingCapacity] = useState(null);
  const [checkingBookingCapacity, setCheckingBookingCapacity] = useState(false);
  const [bookingCapacityError, setBookingCapacityError] = useState(null);
  const [selectedBookingDate, setSelectedBookingDate] = useState(null);
  const [sessionBookedOptions, setSessionBookedOptions] = useState({});

  const [activeHoverCard, setActiveHoverCard] = useState(false);
  const [pricingHoverDismissed, setPricingHoverDismissed] = useState(false);
  const hoverTimeoutRef = useRef(null);

  const handleMouseEnterCard = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setPricingHoverDismissed(false);
    setActiveHoverCard(true);
  };

  const handleMouseLeaveCard = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setActiveHoverCard(false);
    }, 150);
  };

  const handleClosePricingHoverCard = event => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setActiveHoverCard(false);
    setPricingHoverDismissed(true);
  };

  const handleTogglePricingHoverCard = event => {
    if (event?.target?.closest?.('a')) return;
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setPricingHoverDismissed(false);
    setActiveHoverCard(prev => !prev);
  };

  useEffect(() => {
    if (!activeHoverCard || typeof document === 'undefined') return undefined;

    const handlePointerDownOutside = event => {
      if (event.target.closest?.('a[href^="mailto:"]')) return;
      if (event.target.closest?.(`.${css.pricingHoverCard}`)) return;
      if (event.target.closest?.(`.${css.pricingInfoItem}`)) return;
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      setActiveHoverCard(false);
    };

    document.addEventListener('mousedown', handlePointerDownOutside);
    return () => document.removeEventListener('mousedown', handlePointerDownOutside);
  }, [activeHoverCard]);

  const getCurrentDateStr = bookingStartTime => {
    if (bookingStartTime) {
      if (typeof bookingStartTime === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(bookingStartTime.slice(0, 10))) {
        return bookingStartTime.slice(0, 10);
      }
      const parsedTime = Number.isInteger(bookingStartTime)
        ? bookingStartTime
        : typeof bookingStartTime === 'string' && /^\d+$/.test(bookingStartTime)
          ? Number.parseInt(bookingStartTime, 10)
          : null;
      if (parsedTime) {
        const dateObj = new Date(parsedTime);
        return timeZone
          ? moment(dateObj).tz(timeZone).format('YYYY-MM-DD')
          : moment(dateObj).format('YYYY-MM-DD');
      }
      const d = new Date(bookingStartTime);
      if (!Number.isNaN(d.getTime())) {
        return timeZone
          ? moment(d).tz(timeZone).format('YYYY-MM-DD')
          : moment(d).format('YYYY-MM-DD');
      }
    }
    if (selectedBookingDate) return selectedBookingDate;
    if (selectedBookingDateRef.current) return selectedBookingDateRef.current;
    return null;
  };

  const getDailyCapacityForDate = (dateOrDay, listingAttributes = {}) => {
    const publicData = listingAttributes?.publicData || {};
    const availabilityPlan = listingAttributes?.availabilityPlan || {};
    const { seats, personPricing = [], priceVariants = [] } = publicData;
    const defaultSeats = (seats && Number.isInteger(seats) && seats > 0) ? seats : 1;

    let planSeats = null;
    let hasPlanForDay = false;
    if (dateOrDay && availabilityPlan.entries && Array.isArray(availabilityPlan.entries)) {
      const dayOfWeekNames = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
      const dateObj = new Date(dateOrDay);
      if (!Number.isNaN(dateObj.getTime())) {
        const dayOfWeekIndex = timeZone ? moment(dateObj).tz(timeZone).day() : dateObj.getDay();
        const dayOfWeekName = dayOfWeekNames[dayOfWeekIndex];
        const planEntry = availabilityPlan.entries.find(e => e.dayOfWeek === dayOfWeekName);
        hasPlanForDay = true;
        if (planEntry) {
          planSeats = (Number.isInteger(planEntry.seats) && planEntry.seats > 0)
            ? planEntry.seats
            : defaultSeats;
        } else {
          planSeats = 0;
        }
      }
    }

    if (hasPlanForDay && planSeats !== null) {
      return planSeats;
    }
    if (seats && Number.isInteger(seats) && seats > 0) {
      return seats;
    }
    if (Array.isArray(personPricing) && personPricing.length > 0) {
      return Math.max(...personPricing.map(p => p.numberOfPeople || 1));
    }
    if (Array.isArray(priceVariants) && priceVariants.length > 0) {
      const guestNums = priceVariants
        .filter(v => /guest|person|people|pax|seat/i.test(v.name || ''))
        .map(v => {
          const m = v.name?.match(/\d+/);
          return m ? parseInt(m[0], 10) : null;
        })
        .filter(Boolean);
      if (guestNums.length > 0) {
        return Math.max(...guestNums);
      }
    }

    return 1000;
  };

  const getActiveBookedOptions = bookingStartTime => {
    const dateStr = getCurrentDateStr(bookingStartTime);
    if (!dateStr) return [];

    const fromMongoMap = allBookedOptionsMap[dateStr] || [];
    const fromPublic = getBookedOptionsForDate(
      publicData.bookedOptions,
      dateStr
    );
    const fromSession = (sessionBookedOptions && dateStr && sessionBookedOptions[dateStr]) || [];
    const fromCapacity =
      bookingCapacity && bookingCapacity.date === dateStr && Array.isArray(bookingCapacity.bookedOptions)
        ? bookingCapacity.bookedOptions
        : [];

    const booked = fromMongoMap.length > 0 ? fromMongoMap : [...fromPublic, ...fromSession, ...fromCapacity];
    return booked;
  };

  const getActiveBookedSeats = bookingStartTime => {
    const dateStr = getCurrentDateStr(bookingStartTime);
    if (!dateStr) return 0;
    const bookedOptions = getActiveBookedOptions(bookingStartTime);
    let total = 0;
    bookedOptions.forEach(opt => {
      const num = typeof opt === 'number' ? opt : parseInt(String(opt).match(/\d+/)?.[0] || '1', 10);
      total += Number.isNaN(num) ? 1 : num;
    });
    return total;
  };
  const [loginModalState, setLoginModalState] = useState({
    isOpen: false,
    email: '',
    guestDetails: null,
    recoveryEmailSent: false,
  });
  const [pendingLoginContinuation, setPendingLoginContinuation] = useState(null);
  const restoredDraftRef = useRef(null);

  // Helper to generate a random secure password for guest registration
  const generateRandomPassword = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()';
    let password = '';
    for (let i = 0; i < 16; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const intl = useIntl();
  const history = useHistory();
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();

  const userPhoneNumber = currentUser?.attributes?.profile?.protectedData?.phoneNumber || '';
  let initialCountryCode = '+1';
  let initialPhoneNumber = '';
  const countryCodesList = getCountryCodes(intl.locale);
  let initialCountry = '';
  if (userPhoneNumber) {
    if (userPhoneNumber.startsWith('+')) {
      const match = userPhoneNumber.match(/^(\+\d{1,4})(.*)$/);
      if (match) {
        initialCountryCode = match[1];
        initialPhoneNumber = match[2].trim();
      } else {
        initialPhoneNumber = userPhoneNumber;
      }
    } else {
      initialPhoneNumber = userPhoneNumber;
    }
  }

  if (!initialCountry) {
    if (initialCountryCode === '+91') {
      initialCountry = 'India';
    } else if (initialCountryCode === '+1') {
      initialCountry = 'United States';
    } else if (initialCountryCode === '+44') {
      initialCountry = 'United Kingdom';
    } else if (initialCountryCode === '+61') {
      initialCountry = 'Australia';
    } else if (initialCountryCode === '+86') {
      initialCountry = 'China';
    }
  }

  const initialGuestValues = {
    fullName: currentUser?.attributes?.profile
      ? `${currentUser.attributes.profile.firstName || ''} ${currentUser.attributes.profile
        .lastName || ''}`.trim() || currentUser.attributes.profile.displayName
      : '',
    email: currentUser?.attributes?.email || '',
    countryCode: initialCountryCode,
    phoneNumber: initialPhoneNumber,
    country: initialCountry,
  };

  const cardSectionRef = useRef(null);

  const scrollToFormCard = () => {
    if (typeof window !== 'undefined') {
      const el = document.getElementById('form') || cardSectionRef.current;
      if (el) {
        const y = el.getBoundingClientRect().top + window.pageYOffset - 90;
        window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
      }
    }
  };

  useEffect(() => {
    setMounted(true);
    if (typeof window !== 'undefined') {
      const timer = setTimeout(() => {
        scrollToFormCard();
      }, 150);
      return () => clearTimeout(timer);
    }
  }, []);

  const listingSlug = rawParams.slug || createSlug(currentListing?.attributes?.title || '');
  const trackingListingTitle = currentListing?.attributes?.title;

  const [googleCalendarError, setGoogleCalendarError] = useState(null);
  const [checkingGoogleCalendar, setCheckingGoogleCalendar] = useState(false);
  const [isHeroSubtitleExpanded, setIsHeroSubtitleExpanded] = useState(false);
  const prevValuesRef = useRef({ startTime: null, endTime: null });
  const bookingCapacityRequestRef = useRef(0);
  const selectedBookingDateRef = useRef(null);
  const priceSectionRef = useRef(null);
  const reservationPageStartedAtRef = useRef(null);
  const priceViewedRef = useRef(false);

  useEffect(() => {
    const priceSection = priceSectionRef.current;
    if (
      !currentListing?.id?.uuid ||
      !trackingListingTitle ||
      !priceSection ||
      typeof IntersectionObserver === 'undefined'
    ) {
      return undefined;
    }

    trackMixpanelEvent(MIXPANEL_EVENTS.RESERVATION_SUMMARY_VIEWED, {
      ...listingMixpanelProperties(currentListing),
    });

    reservationPageStartedAtRef.current =
      typeof performance !== 'undefined' ? performance.now() : Date.now();
    priceViewedRef.current = false;

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting) && !priceViewedRef.current) {
          priceViewedRef.current = true;
          const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
          const listingPrice = currentListing.attributes?.price;
          trackMixpanelEvent(MIXPANEL_EVENTS.PRICE_VIEWED, {
            ...listingMixpanelProperties(currentListing),
            price_view_location: 'reservation_details',
            booking_type: storedBookingType,
            amount: listingPrice?.amount || null,
            currency: listingPrice?.currency || null,
            seconds_on_reservation_page: reservationPageStartedAtRef.current
              ? Math.round((now - reservationPageStartedAtRef.current) / 1000)
              : null,
          });
          observer.disconnect();
        }
      },
      { threshold: 0.25 }
    );

    observer.observe(priceSection);
    return () => observer.disconnect();
  }, [currentListing?.id?.uuid, trackingListingTitle, storedBookingType]);

  const checkBookingCapacity = async selectedCalendarDate => {
    const requestId = bookingCapacityRequestRef.current + 1;
    bookingCapacityRequestRef.current = requestId;

    if (!selectedCalendarDate) {
      setBookingCapacity(null);
      setBookingCapacityError(null);
      setCheckingBookingCapacity(false);
      setGoogleCalendarError(null);
      return false;
    }

    setCheckingBookingCapacity(false);
    setBookingCapacityError(null);
    setGoogleCalendarError(null);

    const slot = allTimeSlots.find(s => {
      if (!s || !s.attributes || !s.attributes.start) return false;
      const slotDateStr = timeZone
        ? moment(s.attributes.start).tz(timeZone).format('YYYY-MM-DD')
        : moment(s.attributes.start).format('YYYY-MM-DD');
      return slotDateStr === selectedCalendarDate;
    });

    const isAvailable = !!(slot && slot.attributes && slot.attributes.seats > 0);
    setBookingCapacity({ isAvailable, date: selectedCalendarDate, remainingSeats: slot?.attributes?.seats || 0 });
    return isAvailable;
  };

  const handleBookingDateChange = date => {
    const selectedCalendarDate = date ? moment(date).format('YYYY-MM-DD') : null;

    if (selectedCalendarDate === selectedBookingDateRef.current) {
      return;
    }

    selectedBookingDateRef.current = selectedCalendarDate;
    setSelectedBookingDate(selectedCalendarDate);
    setBookingCapacity(null);
    setBookingCapacityError(null);
    setGoogleCalendarError(null);
    setCheckingGoogleCalendar(false);

    if (!selectedCalendarDate) {
      bookingCapacityRequestRef.current += 1;
      setBookingCapacity(null);
      setBookingCapacityError(null);
      setCheckingBookingCapacity(false);
      return;
    }

    checkBookingCapacity(selectedCalendarDate);
  };

  useEffect(() => {
    if (selectedBookingDate && !bookingCapacity) {
      checkBookingCapacity(selectedBookingDate);
    }
  }, [selectedBookingDate, bookingCapacity]);

  const handleFormValuesChange = async formState => {
    const { bookingStartTime, bookingEndTime } = formState.values;
    if (
      bookingStartTime === prevValuesRef.current.startTime &&
      bookingEndTime === prevValuesRef.current.endTime
    ) {
      return;
    }
    prevValuesRef.current = { startTime: bookingStartTime, endTime: bookingEndTime };

    const listingPubData = currentListing?.attributes?.publicData || {};
    const idsArray = listingPubData.serviceProviderIds || listingPubData.serviceproviderIds || [];
    let providerIds = [];
    if (Array.isArray(idsArray) && idsArray.length > 0) {
      providerIds = idsArray.map(p => p.id).filter(Boolean);
    } else {
      const singleId = listingPubData.serviceProviderId || listingPubData.serviceproviderId;
      if (singleId) {
        providerIds = [singleId];
      }
    }

    if (providerIds.length === 0 || !bookingStartTime) {
      setGoogleCalendarError(null);
      return;
    }

    const startDate = timestampToDate(Number.parseInt(bookingStartTime, 10));
    const endDate = bookingEndTime ? timestampToDate(Number.parseInt(bookingEndTime, 10)) : null;

    setCheckingGoogleCalendar(true);
    setGoogleCalendarError(null);

    try {
      let datesToCheck = [];
      if (startDate && endDate) {
        let current = new Date(startDate.getTime());
        while (current < endDate) {
          datesToCheck.push(stringifyDateToISO8601(current, timeZone));
          current = new Date(current.getTime() + 24 * 60 * 60 * 1000);
        }
      } else {
        datesToCheck.push(stringifyDateToISO8601(startDate, timeZone));
      }

      const listingIdStr = currentListing?.id?.uuid || currentListing?.id?.toString();
      for (const dateStr of datesToCheck) {
        const response = await fetchGoogleCalendarAvailability({
          date: dateStr,
          timezone: timeZone,
          providerIds: providerIds.join(','),
          listingId: listingIdStr,
        });

        if (response.hasTokens === false) {
          setGoogleCalendarError(null);
          setCheckingGoogleCalendar(false);
          return;
        }

        if (response.available === false || (response.busy && response.busy.length > 0 && (!response.slots || response.slots.length === 0))) {
          setGoogleCalendarError(
            response.error ||
            intl.formatMessage({
              id: 'BookingTimeForm.providerNotAvailableOnDate',
              defaultMessage:
                'Selected time is not available because one or more service providers are unavailable.',
            })
          );
          setCheckingGoogleCalendar(false);
          return;
        }
      }
    } catch (err) {
      console.error('Error checking Google Calendar availability:', err);
    } finally {
      setCheckingGoogleCalendar(false);
    }
  };

  const availabilityPlan = currentListing?.attributes?.availabilityPlan || {};
  const timeZone = availabilityPlan.timezone || 'Etc/UTC';
  const { unitType, priceVariants = [] } = publicData;

  useEffect(() => {
    if (
      !currentUser?.id?.uuid ||
      !currentListing?.id?.uuid ||
      pendingLoginContinuation
    ) {
      return;
    }

    const draft = getBookingResumeDraft();
    if (!draft || draft.listingId !== currentListing?.id?.uuid) {
      return;
    }

    restoreBookingFlowSession(draft);

    const restoredBookingData = {
      bookingStartTime: draft.bookingData?.bookingStartTime || null,
      bookingEndTime: draft.bookingData?.bookingEndTime || null,
      numberOfGuests: String(draft.bookingData?.numberOfGuests || ''),
      // Preserve applied promotional discount across draft restoration
      discountCode: draft.bookingData?.discountCode || null,
      discountPercentage: draft.bookingData?.discountPercentage || null,
    };
    const parsedStartTime = restoredBookingData.bookingStartTime
      ? parseDate(restoredBookingData.bookingStartTime)
      : null;
    const restoredDate = parsedStartTime ? stringifyDateToISO8601(parsedStartTime, timeZone) : null;
    const restoredLocalDate = restoredDate ? moment(restoredDate, 'YYYY-MM-DD').toDate() : null;
    const restoredBookingStartDate = restoredLocalDate
      ? {
        date:
          unitType === 'hour' || unitType === 'fixed'
            ? timeOfDayFromLocalToTimeZone(restoredLocalDate, timeZone)
            : restoredLocalDate,
      }
      : null;

    setBookingData({
      ...restoredBookingData,
      bookingStartDate: restoredBookingStartDate,
    });
    if (draft.guestDetails) {
      setGuestData(draft.guestDetails);
    }
    setSelectedGuests(restoredBookingData.numberOfGuests);
    setSelectedBookingDate(restoredDate);
    selectedBookingDateRef.current = restoredDate;
    const finishRestoration = async () => {
      const dateAvailable = restoredDate ? await checkBookingCapacity(restoredDate) : true;
      setShowDetailsForm(false);
      setShowReservationSummary(dateAvailable);
      clearBookingResumeDraft();
      if (typeof window !== 'undefined') {
        scrollToFormCard(80);
      }
    };

    finishRestoration();
  }, [
    currentListing?.id?.uuid,
    currentUser?.id?.uuid,
    pendingLoginContinuation,
    timeZone,
    unitType,
  ]);

  useEffect(() => {
    const { userType, guestDetails } = location.state || {};
    const storedUserType = userType || (currentUser || guestDetails ? 'Customer' : null);
  }, [isConversation, location.state, currentUser]);

  useEffect(() => {
    if (listingId && timeZone && currentListing?.id) {
      const TODAY = new Date();
      const startOfToday = getStartOf(TODAY, 'day', timeZone);

      const isFixed = unitType === 'fixed';

      const nextBoundary = startOfToday;
      const nextMonth = getStartOf(nextBoundary, 'month', timeZone, 1, 'months');
      const nextAfterNextMonth = getStartOf(nextMonth, 'month', timeZone, 1, 'months');

      const variants = priceVariants || [];
      const bookingLengthInMinutes = variants.reduce((min, priceVariant) => {
        return Math.min(min, priceVariant.bookingLengthInMinutes);
      }, Number.MAX_SAFE_INTEGER);

      const nextMonthEnd = isFixed
        ? getStartOf(nextMonth, 'minute', timeZone, bookingLengthInMinutes, 'minutes')
        : nextMonth;
      const followingMonthEnd = isFixed
        ? getStartOf(nextAfterNextMonth, 'minute', timeZone, bookingLengthInMinutes, 'minutes')
        : nextAfterNextMonth;

      const minDurationStartingInInterval = isFixed ? bookingLengthInMinutes : 60;

      const options = intervalAlign => {
        return ['fixed', 'hour'].includes(unitType)
          ? {
            extraQueryParams: {
              intervalDuration: 'P1D',
              intervalAlign,
              seats: 0,
              maxPerInterval: 1,
              minDurationStartingInInterval,
              perPage: 31,
              page: 1,
            },
          }
          : null;
      };

      onFetchTimeSlots(listingId, nextBoundary, nextMonthEnd, timeZone, options(startOfToday));
      onFetchTimeSlots(listingId, nextMonth, followingMonthEnd, timeZone, options(nextMonth));
    }
  }, [currentListing?.id, timeZone, unitType, priceVariants]);

  useEffect(() => {
    if (!currentUser?.id?.uuid || !pendingLoginContinuation) {
      return;
    }

    const { guestDetails: pendingGuestDetails } = pendingLoginContinuation;
    clearBookingResumeDraft();
    setSignupError(null);
    setLoginModalState({
      isOpen: false,
      email: '',
      guestDetails: null,
      recoveryEmailSent: false,
    });
    setPendingLoginContinuation(null);
    continueAfterAuthentication(pendingGuestDetails);
  }, [currentUser?.id?.uuid, pendingLoginContinuation]);

  const handleMonthChange = React.useCallback(
    monthDate => {
      if (!listingId || !timeZone) return;

      const TODAY = new Date();
      const startOfToday = getStartOf(TODAY, 'day', timeZone);

      const monthStart = getStartOf(monthDate, 'month', timeZone);
      const adjustedStart = monthStart.getTime() < startOfToday.getTime() ? startOfToday : monthStart;

      const nextMonth = getStartOf(monthStart, 'month', timeZone, 1, 'months');
      const nextAfterNextMonth = getStartOf(monthStart, 'month', timeZone, 2, 'months');

      const isFixed = unitType === 'fixed';
      const variants = priceVariants || [];
      const bookingLengthInMinutes = variants.reduce((min, priceVariant) => {
        return Math.min(min, priceVariant.bookingLengthInMinutes);
      }, Number.MAX_SAFE_INTEGER);

      const currentMonthEnd = isFixed
        ? getStartOf(nextMonth, 'minute', timeZone, bookingLengthInMinutes, 'minutes')
        : nextMonth;
      const followingMonthEnd = isFixed
        ? getStartOf(nextAfterNextMonth, 'minute', timeZone, bookingLengthInMinutes, 'minutes')
        : nextAfterNextMonth;

      const minDurationStartingInInterval = isFixed ? bookingLengthInMinutes : 60;

      const fetchOptions = intervalAlign => {
        return ['fixed', 'hour'].includes(unitType)
          ? {
            extraQueryParams: {
              intervalDuration: 'P1D',
              intervalAlign,
              seats: 0,
              maxPerInterval: 1,
              minDurationStartingInInterval,
              perPage: 31,
              page: 1,
            },
          }
          : null;
      };

      onFetchTimeSlots(listingId, adjustedStart, currentMonthEnd, timeZone, fetchOptions(adjustedStart));
      onFetchTimeSlots(listingId, nextMonth, followingMonthEnd, timeZone, fetchOptions(nextMonth));
    },
    [listingId, timeZone, unitType, priceVariants, onFetchTimeSlots]
  );

  const handleBack = () => {
    if (window.history.length > 1) {
      history.goBack();
    } else {
      history.push(
        createResourceLocatorString(
          'BookingFlowPage',
          routeConfiguration,
          { id: listingId.uuid, slug: listingSlug },
          {}
        )
      );
    }
  };

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing?.id) {
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const { price = null, title = '', description = '' } = currentListing?.attributes || {};

  const listingSubtitle = description || publicData.subtitle || '';
  const heroLocationLine = publicData.location?.address || 'Khao Yai National Park';

  // Responsive hero images — same screen-size sources as ListingPageCarousel / ReservationDetailsPage
  const { goldenSection = {}, detailsSection = {} } = publicData;
  let customHeroImageUrl = null;
  let customHeroImageTabletUrl = null;
  let customHeroImageMobileUrl = null;

  if (publicData.screen_experience === 'golden_screen') {
    // Reservation flow always shows the details-section hero (like listing "show details")
    customHeroImageUrl = detailsSection?.heroImage?.url || goldenSection?.heroImage?.url;
    customHeroImageTabletUrl =
      detailsSection?.heroImageTab?.url || goldenSection?.heroImageTab?.url;
    customHeroImageMobileUrl =
      detailsSection?.heroImageMobile?.url || goldenSection?.heroImageMobile?.url;
  } else if (publicData.screen_experience === 'simple_screen') {
    customHeroImageUrl = detailsSection?.heroImage?.url;
    customHeroImageTabletUrl = detailsSection?.heroImageTab?.url;
    customHeroImageMobileUrl = detailsSection?.heroImageMobile?.url;
  }

  const heroImageUrl =
    customHeroImageUrl ||
    publicData.heroImageCloudinaryUrl ||
    require('../../assets/listingbackgroundcss.jpg');
  const heroImageTabletUrlOriginal = customHeroImageTabletUrl || heroImageUrl;
  const heroImageMobileUrlOriginal = customHeroImageMobileUrl || heroImageUrl;

  const isCloudinaryHero =
    heroImageUrl && typeof heroImageUrl === 'string' && heroImageUrl.includes('res.cloudinary.com');
  const optimizedHeroImageUrl = isCloudinaryHero
    ? optimizeCloudinaryUrl(heroImageUrl, 1920, '16:9', 'best')
    : heroImageUrl;
  const heroImageSrcSet = isCloudinaryHero
    ? getCloudinarySrcSet(heroImageUrl, '16:9', DESKTOP_HERO_SRCSET_WIDTHS, 'best')
    : null;

  const isCloudinaryTablet =
    heroImageTabletUrlOriginal &&
    typeof heroImageTabletUrlOriginal === 'string' &&
    heroImageTabletUrlOriginal.includes('res.cloudinary.com');
  const heroImageTabletUrl = isCloudinaryTablet
    ? optimizeCloudinaryUrl(heroImageTabletUrlOriginal, 1240, '3:4', 'best')
    : heroImageTabletUrlOriginal;
  const heroImageTabletSrcSet = isCloudinaryTablet
    ? getCloudinarySrcSet(heroImageTabletUrlOriginal, '3:4', TABLET_SRCSET_WIDTHS, 'best')
    : null;

  const isCloudinaryMobile =
    heroImageMobileUrlOriginal &&
    typeof heroImageMobileUrlOriginal === 'string' &&
    heroImageMobileUrlOriginal.includes('res.cloudinary.com');
  const heroImageMobileUrl = isCloudinaryMobile
    ? optimizeCloudinaryUrl(heroImageMobileUrlOriginal, 800, '9:16', 'best')
    : heroImageMobileUrlOriginal;
  const heroImageMobileSrcSet = isCloudinaryMobile
    ? getCloudinarySrcSet(heroImageMobileUrlOriginal, '9:16', MOBILE_SRCSET_WIDTHS, 'best')
    : null;

  const { days = [], startTimeInterval, experienceDuration } = publicData;
  const firstVariant = priceVariants[0] || {};
  const isFlat = firstVariant && 'numberOfPeople' in firstVariant;
  const personPricing =
    publicData.personPricing && publicData.personPricing.length > 0
      ? publicData.personPricing
      : (isFlat ? priceVariants : firstVariant.personPricing) || [];

  const hasOnePersonPricingInVariants = personPricing.some(p => p.numberOfPeople === 1);
  const formatPriceFromSubunits = priceInSubunits => {
    const priceInDollars = priceInSubunits / 100;
    return `US $${priceInDollars.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  };
  const bookingLengthInMinutes =
    publicData.bookingLengthInMinutes || firstVariant.bookingLengthInMinutes || 0;

  const variants = priceVariants || [];
  const minBookingLength = variants.reduce((min, priceVariant) => {
    return Math.min(min, priceVariant.bookingLengthInMinutes);
  }, Number.MAX_SAFE_INTEGER);
  const minDurationStartingInInterval = unitType === 'fixed' ? minBookingLength : 60;

  // Extract tour details
  const times =
    publicData.days?.flatMap(day => day.timeline?.map(item => item.startTime || item.time)) || [];

  const getDaysFromMinutes = minutes => {
    const hours = minutes / 60;
    if (hours < 24) return 0.5;
    return hours / 24;
  };

  const tourDays = bookingLengthInMinutes > 0 ? getDaysFromMinutes(bookingLengthInMinutes) : 0;

  const formatAmPmOnly = timeStr => {
    if (!timeStr) return 'AM';
    const str = String(timeStr).trim().toUpperCase();

    if (str === 'AM' || str === 'PM') return str;

    if (str.includes('AFTERNOON') || str.includes('EVENING') || str.includes('NIGHT') || str.includes('PM')) {
      return 'PM';
    }
    if (str.includes('MORNING') || str.includes('AM')) {
      return 'AM';
    }

    const match = str.match(/^(\d{1,2})[:.]?(\d{2})?/);
    if (match) {
      const hours = parseInt(match[1], 10);
      if (!isNaN(hours)) {
        return hours >= 12 ? 'PM' : 'AM';
      }
    }
    return 'AM';
  };

  const expType = publicData.experience_type;
  const tourType = publicData?.tourDays || '';

  const startTime = times.length > 0 ? formatAmPmOnly(times[0]) : formatAmPmOnly(publicData?.experienceStartTime) || 'AM';

  const rawTransportMode = publicData?.transporationMode || publicData?.transportationMode || publicData?.transportation_mode;
  const modeOfTransport = rawTransportMode
    ? (Array.isArray(rawTransportMode) ? rawTransportMode.join(', ') : rawTransportMode)
    : 'Land+';

  const listingPriceAmount = price?.amount || 0;

  // Handle line items fetching
  const handleFetchLineItems = formValues => {
    const values = formValues?.values || formValues || {};
    const { bookingStartTime, bookingEndTime } = values;
    const numberOfGuests = values.numberOfGuests || formValues?.numberOfGuests || selectedGuests;

    const parsedNum = parseInt(numberOfGuests, 10);
    const guestsNum = Number.isNaN(parsedNum) ? 1 : parsedNum;
    // Find matching price variant based on guest count if personPricing is empty
    let priceVariantName = null;
    if (personPricing.length === 0 && priceVariants && priceVariants.length > 0) {
      const match = priceVariants.find(variant => {
        const variantGuests = parseInt(variant.name?.match(/\d+/)?.[0], 10);
        return variantGuests === guestsNum;
      });
      if (match) {
        priceVariantName = match.name;
      }
    }

    const startDate = bookingStartTime ? parseDate(bookingStartTime) : null;
    const isDay = unitType === 'day';
    const isHour = unitType === 'hour';
    const isFixed = unitType === 'fixed';

    // For 'day' bookings: auto-compute endDate as start of next day (exclusive end per Sharetribe API)
    let endDate = bookingEndTime ? parseDate(bookingEndTime) : null;
    if (isDay && startDate && !endDate) {
      endDate = getStartOf(startDate, 'day', timeZone, 1, 'days');
    }

    const isStartBeforeEnd = !startDate || !endDate || startDate < endDate;

    // For 'hour' and 'fixed', require an explicit end time; for 'day', end is auto-computed
    const hasRequiredDates =
      isHour || isFixed ? startDate && endDate && bookingEndTime : startDate && endDate;

    if (hasRequiredDates && isStartBeforeEnd && numberOfGuests && !fetchLineItemsInProgress) {
      const orderData = {
        bookingStart: startDate,
        bookingEnd: endDate,
        numberOfGuests: guestsNum,
        bookingType: storedBookingType,
        ...(priceVariantName ? { priceVariantName } : {}),
      };
      onFetchTransactionLineItems({
        orderData,
        listingId: currentListing?.id,
        isOwnListing: false,
      });
    }
  };

  const handleGuestChange = (value, form) => {
    setSelectedGuests(value);

    // Get current form values
    const formValues = form.getState().values;

    // For 'hour' and 'fixed': require both start and end time. For 'day': only start date needed.
    const isHour = unitType === 'hour';
    const isFixed = unitType === 'fixed';
    if (
      value &&
      formValues.bookingStartTime &&
      (!(isHour || isFixed) || formValues.bookingEndTime)
    ) {
      handleFetchLineItems({
        ...formValues,
        numberOfGuests: value,
      });
    }
  };

  const handleSubmit = async values => {
    if (!values.numberOfGuests) {
      alert(intl.formatMessage({ id: 'ReservationDetailsPage.selectGuestsError' }));
      return;
    }

    const selectedCalendarDate =
      selectedBookingDateRef.current ||
      (values.bookingStartTime
        ? stringifyDateToISO8601(parseDate(values.bookingStartTime), timeZone)
        : null);
    const hasBookingCapacity = await checkBookingCapacity(selectedCalendarDate);
    if (!hasBookingCapacity) {
      return;
    }

    // Store booking data and show details form
    setBookingData(values);
    setAccountableData(null);
    setShowDetailsForm(true);
    if (typeof window !== 'undefined') {
      document.getElementById('form')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const getBookingFlowContext = () => {
    const { userType, userId, bookingType: locationBookingType } = location.state || {};
    return {
      userType: userType || null,
      userId: userId || null,
      bookingType: locationBookingType || 'reservation',
    };
  };

  const saveCurrentBookingResume = (email, guestDetails) => {
    const flowContext = getBookingFlowContext();
    return saveBookingResumeDraft({
      email,
      listingId: currentListing?.id?.uuid,
      listingSlug,
      bookingType: flowContext.bookingType,
      userType: flowContext.userType,
      userId: flowContext.userId,
      bookingData,
      guestDetails: guestDetails || guestData,
      restoreStage: 'reservation-summary',
    });
  };

  const continueAfterAuthentication = details => {
    const flowContext = getBookingFlowContext();
    const currentIsConversation =
      isConversation || flowContext.bookingType === 'conversation';

    if (currentIsConversation) {
      setSubmittingDetails(true);

      const { fullName, countryCode, phoneNumber } = details;
      const nameParts = fullName.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || ' ';

      onUpdateProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim() || ' ',
        protectedData: {
          phoneNumber: `${countryCode}${phoneNumber}`.trim(),
        },
      })
        .then(() => handleProceedToCheckout(bookingData))
        .catch(err => {
          console.error('Failed to update profile before checkout:', err);
          handleProceedToCheckout(bookingData);
        });
    } else {
      setShowDetailsForm(false);
      setShowReservationSummary(true);
      if (typeof window !== 'undefined') {
        scrollToFormCard(80);
      }
    }
  };

  const handleBookingLogin = password => {
    const { email, guestDetails } = loginModalState;
    setPendingLoginContinuation({ guestDetails });
    return onLogin(email, password).catch(error => {
      setPendingLoginContinuation(null);
      throw error;
    });
  };

  const handleForgotPassword = () => {
    const { email, guestDetails } = loginModalState;
    saveCurrentBookingResume(email, guestDetails);
    return onRecoverPassword(email).then(() => {
      setLoginModalState(previous => ({ ...previous, recoveryEmailSent: true }));
    });
  };

  const handleDetailsSubmit = guestDetails => {
    const flowContext = getBookingFlowContext();
    const isConcierge = flowContext.userType === 'Concierge';

    // Store guest details
    setGuestData(guestDetails);

    if (!currentUser) {
      setSubmittingDetails(true);
      setSignupError(null);

      const { fullName, email, countryCode, phoneNumber, country } = guestDetails;
      const nameParts = fullName.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || ' ';
      const generatedPassword = generateRandomPassword();

      const signupParams = {
        email: email.trim(),
        password: generatedPassword,
        firstName: firstName.trim(),
        lastName: lastName.trim() || ' ',
        publicData: {
          userType: 'customer',
          isGuest: true,
        },
        protectedData: {
          phoneNumber: `${countryCode}${phoneNumber}`.trim(),
          country,
        },
      };

      const activeBookingData =
        bookingData || getReservationContactSession(rawParams.id).bookingData || null;
      saveReservationContactSession(rawParams.id, {
        bookingType: flowContext.bookingType,
        userType: flowContext.userType || 'Customer',
        userId: flowContext.userId,
        bookingData: activeBookingData,
        guestDetails,
        redirectToCheckout: flowContext.bookingType === 'conversation',
      });

      clearBookingResumeDraft();
      restoredDraftRef.current = 'signup_in_progress';

      onSignup(signupParams)
        .then(() => {
          const flowContext = getBookingFlowContext();
          const currentIsConversation =
            isConversation || flowContext.bookingType === 'conversation';

          if (currentIsConversation) {
            clearBookingResumeDraft();
            setSubmittingDetails(false);
            setShowDetailsForm(false);
            setTimeout(() => {
              handleProceedToCheckout(activeBookingData, guestDetails);
            }, 150);
          } else {
            setSubmittingDetails(false);
            setShowDetailsForm(false);
            setShowReservationSummary(true);
            if (typeof window !== 'undefined') {
              scrollToFormCard(80);
            }
          }
        })
        .catch(err => {
          console.error('Failed to create customer during guest checkout:', err);
          setSubmittingDetails(false);
          setShowReservationSummary(false);
          setShowDetailsForm(true);
          if (isSignupEmailTakenError(err)) {
            saveCurrentBookingResume(email, guestDetails);
            setLoginModalState({
              isOpen: true,
              email: email.trim().toLowerCase(),
              guestDetails,
              recoveryEmailSent: false,
            });
          } else {
            setSignupError(err);
          }
        });
    } else {
      continueAfterAuthentication(guestDetails);
    }
  };

  const handleProceedToCheckout = (customBookingData, customGuestData) => {
    setShowDetailsForm(false);
    clearBookingResumeDraft();

    const isEvent =
      customBookingData && (customBookingData.preventDefault || customBookingData.target);
    const dataToUse = customBookingData && !isEvent ? customBookingData : bookingData;
    const guestDataToUse = customGuestData || guestData;

    // Retrieve userType, userId and bookingType from state
    const { userType, userId, bookingType } = location.state || {};
    const storedUserType = userType || null;
    const storedUserId = userId || null;
    const storedBookingType = bookingType || 'reservation';

    const selectedPrice = getSelectedPrice(dataToUse?.numberOfGuests);
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    trackMixpanelEvent(MIXPANEL_EVENTS.CHECKOUT_STARTED, {
      ...listingMixpanelProperties(currentListing),
      booking_type: storedBookingType,
      seats: Number.parseInt(dataToUse?.numberOfGuests, 10) || null,
      amount: selectedPrice?.amount || null,
      currency: selectedPrice?.currency || null,
      seconds_on_reservation_page: reservationPageStartedAtRef.current
        ? Math.round((now - reservationPageStartedAtRef.current) / 1000)
        : null,
    });

    // Prepare booking dates
    let checkoutStartDate = dataToUse?.bookingStartTime
      ? parseDate(dataToUse.bookingStartTime)
      : null;

    if (!checkoutStartDate && (isConversation || storedBookingType === 'conversation')) {
      const tomorrow = moment().add(1, 'days').toDate();
      checkoutStartDate = getStartOf(tomorrow, 'day', timeZone);
    }

    // Auto-compute endDate if missing so CheckoutPage validation always passes
    let checkoutEndDate = dataToUse?.bookingEndTime ? parseDate(dataToUse.bookingEndTime) : null;
    if (checkoutStartDate && !checkoutEndDate) {
      if (unitType === 'day') {
        checkoutEndDate = getStartOf(checkoutStartDate, 'day', timeZone, 1, 'days');
      } else if (unitType === 'hour') {
        const lengthInMins = publicData?.bookingLengthInMinutes || 60;
        checkoutEndDate = new Date(checkoutStartDate.getTime() + lengthInMins * 60 * 1000);
      } else {
        checkoutEndDate = new Date(checkoutStartDate.getTime() + 24 * 60 * 60 * 1000);
      }
    }

    const bookingMaybe =
      checkoutStartDate && checkoutEndDate
        ? {
          bookingDates: {
            bookingStart: checkoutStartDate,
            bookingEnd: checkoutEndDate,
          },
        }
        : {};

    // Prepare quantity
    const parsedQuantity = Number.parseInt(dataToUse?.numberOfGuests, 10);
    const quantity = Number.isNaN(parsedQuantity) ? 1 : parsedQuantity;
    const quantityMaybe = Number.isInteger(quantity) ? { quantity } : {};

    // Find matching price variant based on guest count if personPricing is empty
    let priceVariantName = null;
    if (personPricing.length === 0 && priceVariants && priceVariants.length > 0) {
      const match = priceVariants.find(variant => {
        const variantGuests = parseInt(variant.name?.match(/\d+/)?.[0], 10);
        return variantGuests === quantity;
      });
      if (match) {
        priceVariantName = match.name;
      }
    }

    const isConcierge = storedUserType === 'Concierge';

    // Prepare initial values for checkout
    const initialValues = {
      listing: currentListing,
      orderData: {
        ...bookingMaybe,
        ...quantityMaybe,
        seats: quantity,
        userType: storedUserType,
        userId: storedUserId,
        bookingType: storedBookingType,
        transactionProcessAlias: publicData?.transactionProcessAlias || 'default-booking/release-1',
        tourType,
        startTime,
        modeOfTransport,
        ...(dataToUse?.discountCode
          ? {
              discountCode: dataToUse.discountCode,
              discountPercentage: dataToUse.discountPercentage || 20,
            }
          : {}),
        ...(priceVariantName ? { priceVariantName } : {}),
        accountableName: isConcierge ? (guestDataToUse?.fullName || null) : null,
        accountableEmail: isConcierge ? (guestDataToUse?.email || null) : null,
        accountablePhoneNumber: isConcierge
          ? (guestDataToUse?.phoneNumber
            ? `${guestDataToUse.countryCode || ''}${guestDataToUse.phoneNumber}`.trim()
            : null)
          : null,
        accountableCountry: isConcierge ? (guestDataToUse?.country || null) : null,
      },
      confirmPaymentError: null,
    };

    const saveToSessionStorage = true;

    // Set initial values for CheckoutPage
    const { setInitialValues: setCheckoutInitialValues } = findRouteByRouteName(
      'CheckoutPage',
      routeConfiguration
    );

    callSetInitialValues(setCheckoutInitialValues, initialValues, saveToSessionStorage);

    // Clear previous Stripe errors from store if there is any
    onInitializeCardPaymentData();

    // Redirect to CheckoutPage
    const checkoutUrl = createResourceLocatorString(
      'CheckoutPage',
      routeConfiguration,
      { id: currentListing?.id?.uuid, slug: createSlug(currentListing?.attributes?.title || '') },
      {}
    );

    history.push(checkoutUrl, {
      orderData: initialValues.orderData,
      listing: currentListing,
    });
  };

  const getSelectedPrice = guestsCount => {
    if (!guestsCount) return price;
    const guestsNum = parseInt(guestsCount, 10);
    const option = personPricing?.find(p => p.numberOfPeople === guestsNum);
    if (option) {
      return new Money(option.priceInSubunits, price.currency);
    }
    if (priceVariants && priceVariants.length > 0) {
      const match = priceVariants.find(variant => {
        const variantGuests = parseInt(variant.name?.match(/\d+/)?.[0], 10);
        return variantGuests === guestsNum;
      });
      if (match) {
        return new Money(match.priceInSubunits, price.currency);
      }
    }
    return price;
  };

  const basePrice = getSelectedPrice(selectedGuests || '1');
  const calculateFractionPrice = (bPrice, fraction) => {
    if (!bPrice) return null;
    const amount = Math.round(bPrice.amount * fraction);
    return new Money(amount, bPrice.currency);
  };

  const price30 = calculateFractionPrice(basePrice, 0.3);
  const price40 = calculateFractionPrice(basePrice, 0.4);

  const formatted30 = price30 ? formatMoney(intl, price30) : '';
  const formatted40 = price40 ? formatMoney(intl, price40) : '';
  const formattedJourneyPrice = basePrice ? formatMoney(intl, basePrice) : '';

  const schemaTitle = intl.formatMessage(
    { id: 'ReservationDetailsPage.schemaTitle' },
    { title, marketplaceName: config.marketplaceName }
  );

  const getGuestNumberWord = num => {
    const words = {
      1: intl.formatMessage({ id: 'ReservationDetailsPage.numOne' }),
      2: intl.formatMessage({ id: 'ReservationDetailsPage.numTwo' }),
      3: intl.formatMessage({ id: 'ReservationDetailsPage.numThree' }),
      4: intl.formatMessage({ id: 'ReservationDetailsPage.numFour' }),
      5: intl.formatMessage({ id: 'ReservationDetailsPage.numFive' }),
      6: intl.formatMessage({ id: 'ReservationDetailsPage.numSix' }),
      7: intl.formatMessage({ id: 'ReservationDetailsPage.numSeven' }),
      8: intl.formatMessage({ id: 'ReservationDetailsPage.numEight' }),
      9: intl.formatMessage({ id: 'ReservationDetailsPage.numNine' }),
      10: intl.formatMessage({ id: 'ReservationDetailsPage.numTen' }),
    };
    return words[num] || String(num);
  };

  const renderGuestCountOptions = (bookingStartTime, bookingEndTime) => {
    const bookingLengthInMinutes = publicData?.bookingLengthInMinutes || 1440;
    const numDays = Math.max(1, Math.round(bookingLengthInMinutes / 1440));

    let minRemaining = 1000;
    if (bookingStartTime) {
      const startTimeMs = typeof bookingStartTime === 'number'
        ? bookingStartTime
        : new Date(bookingStartTime).getTime();
      if (!Number.isNaN(startTimeMs)) {
        for (let i = 0; i < numDays; i++) {
          const dObj = new Date(startTimeMs + i * 24 * 60 * 60 * 1000);
          const dayCap = getDailyCapacityForDate(dObj, currentListing?.attributes);
          const dayBooked = getActiveBookedSeats(dObj.getTime());
          const dayRem = Math.max(0, dayCap - dayBooked);
          if (dayRem < minRemaining) {
            minRemaining = dayRem;
          }
        }
      }
    }
    let remainingSeats = Math.max(0, minRemaining);
    if (bookingCapacity && typeof bookingCapacity.remainingSeats === 'number') {
      remainingSeats = bookingCapacity.remainingSeats;
    }

    const titleAfterHyphen = getTitleAfterHyphen(title);
    const isMultipleDay = expType === 'multiple_day';

    if (personPricing.length > 0) {
      return [...personPricing]
        .sort((a, b) => a.numberOfPeople - b.numberOfPeople)
        .filter(option => option.numberOfPeople <= remainingSeats)
        .map(option => {
          const guestPrice = new Money(option.priceInSubunits, price.currency);
          const formattedPrice = formatMoney(intl, guestPrice);
          const guestLabel = isMultipleDay && titleAfterHyphen
            ? (option.numberOfPeople === 1 ? titleAfterHyphen : `${option.numberOfPeople} ${titleAfterHyphen}`)
            : (option.numberOfPeople === 1
              ? intl.formatMessage({ id: 'ListingPageCarousel.personLabel' })
              : intl.formatMessage({ id: 'ListingPageCarousel.peopleLabel' }));

          const textContent = isMultipleDay && titleAfterHyphen
            ? `${guestLabel} - ${formattedPrice}`
            : `${option.numberOfPeople} ${guestLabel} - ${formattedPrice}`;

          return (
            <option value={option.numberOfPeople} key={option.numberOfPeople}>
              {textContent}
            </option>
          );
        });
    }

    if (priceVariants && priceVariants.length > 0) {
      const items = priceVariants
        .map(variant => {
          const match = variant.name?.match(/\d+/);
          const guestsNum = match ? parseInt(match[0], 10) : null;
          if (guestsNum === null) return null;

          const guestPrice = new Money(variant.priceInSubunits, price.currency);
          const formattedPrice = formatMoney(intl, guestPrice);
          const guestLabel = isMultipleDay && titleAfterHyphen
            ? (guestsNum === 1 ? titleAfterHyphen : `${guestsNum} ${titleAfterHyphen}`)
            : (guestsNum === 1
              ? intl.formatMessage({ id: 'ListingPageCarousel.personLabel' })
              : intl.formatMessage({ id: 'ListingPageCarousel.peopleLabel' }));
          const labelText = isMultipleDay && titleAfterHyphen
            ? `${guestLabel} - ${formattedPrice}`
            : (variant.name ? `${variant.name} - ${formattedPrice}` : `${guestsNum} ${guestLabel} - ${formattedPrice}`);

          return {
            value: guestsNum,
            element: (
              <option value={guestsNum} key={guestsNum}>
                {labelText}
              </option>
            ),
          };
        })
        .filter(Boolean)
        .filter(item => item.value <= remainingSeats);

      const uniqueItems = [];
      const seen = new Set();
      items.forEach(item => {
        if (!seen.has(item.value)) {
          seen.add(item.value);
          uniqueItems.push(item);
        }
      });

      return uniqueItems.sort((a, b) => a.value - b.value).map(item => item.element);
    }

    return [1, 2, 3, 4, 5]
      .filter(num => num <= remainingSeats)
      .map(num => {
        const guestLabel = isMultipleDay && titleAfterHyphen
          ? (num === 1 ? titleAfterHyphen : `${num} ${titleAfterHyphen}`)
          : (num === 1
            ? intl.formatMessage({ id: 'ListingPageCarousel.personLabel' })
            : intl.formatMessage({ id: 'ListingPageCarousel.peopleLabel' }));
        const textContent = isMultipleDay && titleAfterHyphen
          ? guestLabel
          : `${num} ${guestLabel}`;

        return (
          <option value={num} key={num}>
            {textContent}
          </option>
        );
      });
  };

  const getGuestCountOptions = (bookingStartTime, bookingEndTime) => {
    const bookingLengthInMinutes = publicData?.bookingLengthInMinutes || 1440;
    const numDays = Math.max(1, Math.round(bookingLengthInMinutes / 1440));

    let minRemaining = 1000;
    if (bookingStartTime) {
      const startTimeMs = typeof bookingStartTime === 'number'
        ? bookingStartTime
        : new Date(bookingStartTime).getTime();
      if (!Number.isNaN(startTimeMs)) {
        for (let i = 0; i < numDays; i++) {
          const dObj = new Date(startTimeMs + i * 24 * 60 * 60 * 1000);
          const dayCap = getDailyCapacityForDate(dObj, currentListing?.attributes);
          const dayBooked = getActiveBookedSeats(dObj.getTime());
          const dayRem = Math.max(0, dayCap - dayBooked);
          if (dayRem < minRemaining) {
            minRemaining = dayRem;
          }
        }
      }
    }
    let remainingSeats = Math.max(0, minRemaining);
    if (bookingCapacity && typeof bookingCapacity.remainingSeats === 'number') {
      remainingSeats = bookingCapacity.remainingSeats;
    }

    const titleAfterHyphen = getTitleAfterHyphen(title);
    const isMultipleDay = expType === 'multiple_day';

    if (personPricing.length > 0) {
      return [...personPricing]
        .sort((a, b) => a.numberOfPeople - b.numberOfPeople)
        .filter(option => option.numberOfPeople <= remainingSeats)
        .map(option => {
          const guestPrice = new Money(option.priceInSubunits, price.currency);
          const formattedPrice = formatMoney(intl, guestPrice);
          let labelText;
          if (isMultipleDay && titleAfterHyphen) {
            labelText = option.numberOfPeople === 1 ? titleAfterHyphen : `${option.numberOfPeople} ${titleAfterHyphen}`;
          } else {
            const guestLabel = option.numberOfPeople === 1 ? 'Guest' : 'Guests';
            labelText = `${option.numberOfPeople} ${guestLabel}`;
          }

          return {
            value: option.numberOfPeople,
            label: labelText,
            price: formattedPrice,
          };
        });
    }

    if (priceVariants && priceVariants.length > 0) {
      const options = priceVariants
        .map(variant => {
          const match = variant.name?.match(/\d+/);
          const guestsNum = match ? parseInt(match[0], 10) : null;
          if (guestsNum === null) return null;

          const guestPrice = new Money(variant.priceInSubunits, price.currency);
          const formattedPrice = formatMoney(intl, guestPrice);
          let labelText;
          if (isMultipleDay && titleAfterHyphen) {
            labelText = guestsNum === 1 ? titleAfterHyphen : `${guestsNum} ${titleAfterHyphen}`;
          } else {
            const guestLabel = guestsNum === 1 ? 'Guest' : 'Guests';
            labelText = variant.name || `${guestsNum} ${guestLabel}`;
          }

          return {
            value: guestsNum,
            label: labelText,
            price: formattedPrice,
          };
        })
        .filter(Boolean)
        .filter(opt => opt.value <= remainingSeats);

      const uniqueOptions = [];
      const seen = new Set();
      options.forEach(opt => {
        if (!seen.has(opt.value)) {
          seen.add(opt.value);
          uniqueOptions.push(opt);
        }
      });

      return uniqueOptions.sort((a, b) => a.value - b.value);
    }

    return [1, 2, 3, 4, 5]
      .filter(num => num <= remainingSeats)
      .map(num => {
        let labelText;
        if (isMultipleDay && titleAfterHyphen) {
          labelText = num === 1 ? titleAfterHyphen : `${num} ${titleAfterHyphen}`;
        } else {
          labelText = `${num} ${num === 1 ? 'Guest' : 'Guests'}`;
        }

        return {
          value: num,
          label: labelText,
          price: null,
        };
      });
  };

  const renderBookingFormCard = () => (
    <div
      id="form"
      ref={priceSectionRef}
      className={isConversation ? css.conversationCard : css.detailsCard}
    >
      <FinalForm
        onSubmit={handleSubmit}
        initialValues={{
          ...(bookingData || {}),
          numberOfGuests: bookingData?.numberOfGuests || (isConversation ? '1' : ''),
          bookingStartDate: bookingData?.bookingStartDate || null,
          bookingStartTime: bookingData?.bookingStartTime || null,
          bookingEndTime: bookingData?.bookingEndTime || null,
        }}
        render={formRenderProps => {
          const { handleSubmit, form, values, pristine } = formRenderProps;
          const { bookingStartTime, bookingEndTime, numberOfGuests } = values;
          const formattedSelectedDate = (() => {
            if (!selectedBookingDate) return '';
            return moment(selectedBookingDate, 'YYYY-MM-DD').format('D MMMM');
          })();
          const isHour = unitType === 'hour';
          const isFixed = unitType === 'fixed';

          const isDateBooked = (() => {
            if (!bookingStartTime || !monthlyTimeSlots) return false;
            const date = new Date(Number.parseInt(bookingStartTime, 10));
            const localizedDay = timeZone ? timeOfDayFromLocalToTimeZone(date, timeZone) : date;
            const dayInListingTZ = getStartOf(localizedDay, 'day', timeZone);

            const allTimeSlots = Object.values(monthlyTimeSlots || {}).reduce((picked, mts) => {
              return [...picked, ...(mts.timeSlots || [])];
            }, []);

            const slotsForDay = allTimeSlots.filter(ts => {
              const tsStart = new Date(ts.attributes.start);
              return getStartOf(tsStart, 'day', timeZone).getTime() === dayInListingTZ.getTime();
            });

            if (slotsForDay.length > 0) {
              if ((isHour || isFixed) && bookingEndTime) {
                const selStart = new Date(Number.parseInt(bookingStartTime, 10)).getTime();
                const selEnd = new Date(Number.parseInt(bookingEndTime, 10)).getTime();
                const selectedSlot = slotsForDay.find(ts => {
                  const tsStart = new Date(ts.attributes.start).getTime();
                  const tsEnd = new Date(ts.attributes.end).getTime();
                  return tsStart === selStart && tsEnd === selEnd;
                });
                if (selectedSlot) {
                  return selectedSlot.attributes.seats <= 0;
                }
              }
              const hasAvailableSlot = slotsForDay.some(ts => ts.attributes.seats > 0);
              return !hasAvailableSlot;
            }

            return false;
          })();

          // 'hour' and 'fixed' bookings require both start AND end time; 'day' bookings only need start date
          const isSubmitDisabled =
            !numberOfGuests ||
            !bookingStartTime ||
            ((isHour || isFixed) && !bookingEndTime) ||
            isDateBooked ||
            !!googleCalendarError ||
            checkingGoogleCalendar ||
            checkingBookingCapacity ||
            (bookingCapacity ? bookingCapacity.isAvailable === false : false) ||
            !!bookingCapacityError;

          const renderPricingHoverCard = () => {
            const titleAfterHyphen = getTitleAfterHyphen(title);
            const isMultipleDay = expType === 'multiple_day';

            return (
              <div
                className={`${css.pricingHoverCard} ${activeHoverCard ? css.pricingHoverCardActive : ''
                  } ${pricingHoverDismissed ? css.pricingHoverCardDismissed : ''}`}
                onMouseEnter={handleMouseEnterCard}
                onMouseLeave={handleMouseLeaveCard}
                onClick={event => event.stopPropagation()}
              >
                <h3 className={css.pricingHoverTitle}>
                  <FormattedMessage id="ListingPageCarousel.pricingHoverTitle" />
                  <button
                    type="button"
                    className={css.pricingHoverClose}
                    onClick={handleClosePricingHoverCard}
                    aria-label={intl.formatMessage({ id: 'Modal.closeModal' })}
                  >
                    <IconCard brand="close" />
                  </button>
                </h3>

                <div className={css.pricingHoverRows}>
                  <>
                    {!hasOnePersonPricingInVariants && (
                      <div className={css.pricingHoverRow}>
                        <span>
                          {isMultipleDay && titleAfterHyphen ? (
                            titleAfterHyphen
                          ) : (
                            <>
                              1 <FormattedMessage id="ListingPageCarousel.personLabel" />
                            </>
                          )}
                        </span>
                        <span className={css.pricingHoverPrice}>{formatPriceFromSubunits(listingPriceAmount)}</span>
                      </div>
                    )}
                    {personPricing.map((pricing, index) => {
                      const unitPriceInSubunits = pricing.numberOfPeople
                        ? Math.round(pricing.priceInSubunits / pricing.numberOfPeople)
                        : pricing.priceInSubunits;
                      const displayLabel = isMultipleDay && titleAfterHyphen
                        ? (pricing.numberOfPeople === 1 ? titleAfterHyphen : `${pricing.numberOfPeople} ${titleAfterHyphen}`)
                        : null;

                      return (
                        <div key={index} className={css.pricingHoverRow}>
                          <span>
                            {displayLabel ? (
                              displayLabel
                            ) : (
                              <>
                                <b>{pricing.numberOfPeople} </b>
                                <FormattedMessage
                                  id={
                                    pricing.numberOfPeople === 1
                                      ? 'ListingPageCarousel.personLabel'
                                      : 'ListingPageCarousel.peopleLabel'
                                  }
                                />
                              </>
                            )}
                          </span>
                          <span className={css.pricingHoverPrice}>{formatPriceFromSubunits(unitPriceInSubunits)}</span>
                        </div>
                      );
                    })}
                  </>
                </div>

                {!isMultipleDay && (
                  <p className={css.pricingHoverNote}>
                    <FormattedMessage id="ListingPageCarousel.pricingPerPersonNote" />
                  </p>
                )}

                <p className={css.pricingHoverFooter}>
                  <FormattedMessage
                    id="ListingPageCarousel.pricingFooter"
                    values={{
                      email: chunks => (
                        <a
                          href="mailto:info@lamhasia.com"
                          onClick={event => {
                            event.preventDefault();
                            event.stopPropagation();
                            window.location.assign('mailto:info@lamhasia.com');
                          }}
                        >
                          {chunks}
                        </a>
                      ),
                    }}
                  />
                </p>
              </div>
            );
          };

          return (
            <Form
              onSubmit={handleSubmit}
              className={isConversation ? css.conversationBookingForm : css.bookingForm}
            >
              <FormSpy subscription={{ values: true }} onChange={handleFormValuesChange} />
              <div className={isConversation ? css.conversationCardContent : css.cardContent}>
                <button type="button" className={css.backBtn} onClick={handleBack}>
                  <FormattedMessage id="ReservationDetailsPage.backButton" />
                </button>
                {isConversation ? (
                  <>
                    <div className={css.conversationCardHeader}>
                      <div className={css.cardHeaderLabel}>
                        <FormattedMessage id="ReservationDetailsPage.selectedExperience" />
                      </div>
                      <h2 className={css.cardExperienceTitle}>{title}</h2>
                    </div>
                    <div className={css.cardHeaderDivider} />
                  </>
                ) : (
                  <h3 className={css.detailsHeading}>
                    <FormattedMessage id="ReservationDetailsPage.detailsHeading" />
                  </h3>
                )}

                <div className={isConversation ? css.detailsGridConversation : css.detailsGrid}>
                  <div className={css.detailItem}>
                    <div className={css.detailLabel}>
                      <FormattedMessage id="ReservationDetailsPage.tourTypeLabel" />
                    </div>
                    <div className={css.detailValue}>{tourType}</div>
                  </div>

                  <div className={css.detailItem}>
                    <div className={css.detailLabel}>
                      <FormattedMessage id="ReservationDetailsPage.startsAtLabel" />
                    </div>
                    <div className={css.detailValue}>{startTime}</div>
                  </div>

                  <div className={css.detailItem}>
                    <div className={css.detailLabel}>
                      <FormattedMessage id="ReservationDetailsPage.modeOfTransportLabel" />
                    </div>
                    <div className={css.detailValue}>{modeOfTransport}</div>
                  </div>

                  {!isConversation && (
                    <div
                      className={`${css.detailItem} ${css.pricingInfoItem}`}
                      onMouseEnter={handleMouseEnterCard}
                      onClick={handleTogglePricingHoverCard}
                    >
                      <div className={css.pricingLabelRow}>
                        <div className={css.detailLabel}>
                          <FormattedMessage id="ReservationDetailsPage.pricingLabel" />
                        </div>
                        <IconCard brand="info" className={css.infoIcon} onClick={handleTogglePricingHoverCard} />
                      </div>
                      <div className={css.detailValue}>
                        {basePrice ? formatMoney(intl, basePrice) : 'N/A'}
                      </div>
                      <div className={css.detailSubtext}>
                        {selectedGuests || expType === 'multiple_day' ? (
                          ''
                        ) : (
                          <FormattedMessage id="ReservationDetailsPage.onwardsLabel" />
                        )}
                      </div>

                      {renderPricingHoverCard()}
                    </div>
                  )}
                </div>

                <div className={css.cardHeaderDivider} />

                <div
                  className={
                    isConversation ? css.conversationSelectionSection : css.selectionSection
                  }
                >
                  <div className={isConversation ? css.conversationPeopleRow : css.selectionRow}>
                    {(() => {
                      const allTimeSlots = Object.values(monthlyTimeSlots || {}).reduce(
                        (picked, mts) => {
                          return [...picked, ...(mts.timeSlots || [])];
                        },
                        []
                      );

                      const isDayBlocked = day => {
                        const dateStr = day?.format
                          ? day.format('YYYY-MM-DD')
                          : moment(day).format('YYYY-MM-DD');
                        const { isAvailable } = isDateAvailableInSlots(
                          monthlyTimeSlots,
                          dateStr,
                          timeZone,
                          null,
                          null,
                          numDays
                        );
                        return !isAvailable;
                      };

                      return (
                        <div className={css.dateFieldWrapper}>
                          {unitType === 'hour' ? (
                            <FieldDateAndTimeInputHourly
                              key="hourly-picker"
                              seatsEnabled={false}
                              setSeatsOptions={setSeatsOptions}
                              experienceDuration={experienceDuration}
                              startDateInputProps={{
                                label: intl.formatMessage({
                                  id: 'ReservationDetailsPage.selectDateLabel',
                                  defaultMessage: 'Select Date',
                                }),
                                placeholderText: intl.formatMessage({
                                  id: 'ReservationDetailsPage.chooseDatePlaceholder',
                                  defaultMessage: 'Choose Date',
                                }),
                              }}
                              endDateInputProps={{
                                label: intl.formatMessage({
                                  id: 'ReservationDetailsPage.selectEndDateLabel',
                                  defaultMessage: 'End Date',
                                }),
                                placeholderText: intl.formatMessage({
                                  id: 'ReservationDetailsPage.chooseEndDatePlaceholder',
                                  defaultMessage: 'Choose End Date',
                                }),
                              }}
                              className={css.bookingDates}
                              listingId={listingId}
                              onFetchTimeSlots={onFetchTimeSlots}
                              monthlyTimeSlots={monthlyTimeSlots}
                              timeSlotsForDate={timeSlotsForDate}
                              values={values}
                              intl={intl}
                              form={form}
                              pristine={pristine}
                              timeZone={timeZone}
                              dayCountAvailableForBooking={
                                config.stripe?.dayCountAvailableForBooking || 365
                              }
                              handleFetchLineItems={handleFetchLineItems}
                              onBookingDateChange={handleBookingDateChange}
                              isDayBlocked={isDayBlocked}
                            />
                          ) : unitType === 'fixed' ? (
                            <FieldDateAndTimeInputFixed
                              key="fixed-picker"
                              seatsEnabled={false}
                              setSeatsOptions={setSeatsOptions}
                              hideStartTime={true}
                              startDateInputProps={{
                                label: intl.formatMessage({
                                  id: 'ReservationDetailsPage.selectDateLabel',
                                  defaultMessage: 'Select Date',
                                }),
                                placeholderText: intl.formatMessage({
                                  id: 'ReservationDetailsPage.chooseDatePlaceholder',
                                  defaultMessage: 'Choose Date',
                                }),
                              }}
                              className={css.bookingDates}
                              listingId={listingId}
                              startTimeInterval={startTimeInterval}
                              onFetchTimeSlots={onFetchTimeSlots}
                              monthlyTimeSlots={monthlyTimeSlots}
                              timeSlotsForDate={timeSlotsForDate}
                              minDurationStartingInInterval={minDurationStartingInInterval}
                              values={values}
                              priceVariants={priceVariants}
                              intl={intl}
                              form={form}
                              pristine={pristine}
                              timeZone={timeZone}
                              dayCountAvailableForBooking={
                                config.stripe?.dayCountAvailableForBooking || 365
                              }
                              handleFetchLineItems={handleFetchLineItems}
                              onBookingDateChange={handleBookingDateChange}
                            />
                          ) : (
                            // 'day' unit type: simple single-date picker
                            // bookingEndTime is auto-computed as start+1day in handleFetchLineItems
                            (() => {
                              const TODAY = new Date();
                              const dayCountAvailableForBooking =
                                config.stripe?.dayCountAvailableForBooking || 365;
                              const startOfAvailableRangeDate = getStartOf(TODAY, 'day', timeZone);
                              const endOfAvailableRangeDate = getStartOf(
                                TODAY,
                                'day',
                                timeZone,
                                dayCountAvailableForBooking,
                                'days'
                              );

                              const isOutsideRange = day => {
                                const localizedDay = timeOfDayFromLocalToTimeZone(day, timeZone);
                                const dayInListingTZ = getStartOf(localizedDay, 'day', timeZone);
                                const time = dayInListingTZ.getTime();
                                return (
                                  time < startOfAvailableRangeDate.getTime() ||
                                  time > endOfAvailableRangeDate.getTime()
                                );
                              };

                              return (
                                <>
                                  <label className={css.fieldLabel}>
                                    {intl.formatMessage({
                                      id: 'ReservationDetailsPage.selectDateLabel',
                                      defaultMessage: 'Select Date',
                                    })}
                                  </label>
                                  <FieldSingleDatePicker
                                    key="date-picker"
                                    id="bookingStartDate"
                                    name="bookingStartDate"
                                    placeholderText={intl.formatMessage({
                                      id: 'ReservationDetailsPage.chooseDatePlaceholder',
                                      defaultMessage: 'Choose Date',
                                    })}
                                    validate={validators.bookingDateRequired(
                                      intl.formatMessage({
                                        id: 'ReservationDetailsPage.requiredDate',
                                        defaultMessage: 'Please select a date',
                                      })
                                    )}
                                    showErrorMessage={true}
                                    isOutsideRange={isOutsideRange}
                                    isDayBlocked={isDayBlocked}
                                    onMonthChange={handleMonthChange}
                                    onChange={({ date }) => {
                                      handleBookingDateChange(date || null);
                                      if (date) {
                                        // Convert Date to start-of-day timestamp in listing timezone preserving calendar day
                                        const startOfDay = timeZone
                                          ? timeOfDayFromLocalToTimeZone(date, timeZone)
                                          : getStartOf(date, 'day');
                                        const startTimestamp = startOfDay.getTime().toString();
                                        form.change('bookingStartTime', startTimestamp);

                                        const bookingLengthInMinutes = publicData?.bookingLengthInMinutes || 1440;
                                        const numDays = Math.max(1, Math.round(bookingLengthInMinutes / 1440));
                                        const endTimestamp = (startOfDay.getTime() + numDays * 24 * 60 * 60 * 1000).toString();
                                        form.change('bookingEndTime', endTimestamp);

                                        // Trigger line items fetch if guests already selected
                                        if (values.numberOfGuests) {
                                          handleFetchLineItems({
                                            ...values,
                                            bookingStartTime: startTimestamp,
                                            bookingEndTime: endTimestamp,
                                            numberOfGuests: values.numberOfGuests,
                                          });
                                        }
                                      } else {
                                        form.change('bookingStartTime', null);
                                        form.change('bookingEndTime', null);
                                      }
                                    }}
                                  />
                                </>
                              );
                            })()
                          )}

                          {isConversation && isDateBooked ? (
                            <div
                              className={css.dateBookedError}
                              style={{ textAlign: 'left', marginTop: '10px' }}
                            >
                              <FormattedMessage id="ReservationDetailsPage.dateAlreadyBooked" />
                            </div>
                          ) : null}

                          {isConversation && (checkingBookingCapacity || checkingGoogleCalendar) ? (
                            <div className={css.bookingCapacityStatus} role="status">
                              <FormattedMessage id="ReservationDetailsPage.checkingBookingCapacity" />
                            </div>
                          ) : isConversation && googleCalendarError ? (
                            <div className={css.bookingCapacityNotice} role="status">
                              {googleCalendarError}
                            </div>
                          ) : isConversation && bookingCapacity?.isAvailable === false ? (
                            <div className={css.bookingCapacityNotice} role="status">
                              <FormattedMessage
                                id="ReservationDetailsPage.bookingCapacityReached"
                                values={{ date: formattedSelectedDate }}
                              />
                            </div>
                          ) : isConversation && bookingCapacityError ? (
                            <div className={css.bookingCapacityNotice} role="status">
                              {bookingCapacityError}
                            </div>
                          ) : null}
                        </div>
                      );
                    })()}

                    <div className={isConversation ? css.conversationSelectField : css.selectField}>
                      {isConversation ? (
                        <FieldSelect
                          name="numberOfGuests"
                          id="numberOfGuests"
                          label={intl.formatMessage({ id: 'ReservationDetailsPage.numberOfPeopleLabel' })}
                          labelClassName={css.conversationFieldLabel}
                          selectClassName={css.conversationSelect}
                          className={css.conversationGuestsField}
                          onChange={value => handleGuestChange(value, form)}
                        >
                          {renderGuestCountOptions(bookingStartTime, bookingEndTime)}
                        </FieldSelect>
                      ) : (
                        <FieldGuestCountPicker
                          name="numberOfGuests"
                          id="numberOfGuests"
                          label={intl.formatMessage({ id: 'ReservationDetailsPage.numberOfGuestsLabel' })}
                          labelClassName={css.fieldLabel}
                          className={css.fieldGuests}
                          placeholder={intl.formatMessage({
                            id: 'ReservationDetailsPage.selectGuestsPlaceholder',
                          })}
                          options={getGuestCountOptions(bookingStartTime, bookingEndTime)}
                          onChange={value => handleGuestChange(value, form)}
                        />
                      )}
                    </div>

                    {isConversation ? (
                      <div
                        className={`${css.journeyPriceContainer} ${css.pricingInfoItem}`}
                        onMouseEnter={handleMouseEnterCard}
                        onClick={handleTogglePricingHoverCard}
                      >
                        <span className={css.journeyPriceLabel}>
                          <FormattedMessage id="ReservationDetailsPage.journeyPriceLabel" /> <IconCard brand="infoRound" className={css.infoIcon} onClick={handleTogglePricingHoverCard} />
                        </span>
                        <span className={css.journeyPriceValue}>{formattedJourneyPrice}</span>
                        {renderPricingHoverCard()}
                      </div>
                    ) : null}
                  </div>
                </div>

                {isConversation ? (
                  <>
                    <div className={css.cardHeaderDivider} />

                    <div className={css.paymentBreakdown}>
                      <h4 className={css.breakdownHeading}>
                        <FormattedMessage id="ReservationDetailsPage.paymentBreakdownHeading" />
                      </h4>
                      <div className={css.breakdownList}>
                        <div className={`${css.breakdownItem} ${css.breakdownItemActive}`}>
                          <div className={css.breakdownContent}>
                            <span className={css.breakdownLabel}>
                              <FormattedMessage id="ReservationDetailsPage.depositToday" />
                            </span>
                            <span className={css.breakdownValue}>
                              <FormattedMessage id="ReservationDetailsPage.depositTodayValue" values={{ formatted30 }} />
                            </span>
                          </div>
                        </div>

                        <div className={css.breakdownItem}>
                          <div className={css.breakdownContent}>
                            <span className={css.breakdownLabel}>
                              <FormattedMessage id="ReservationDetailsPage.afterConsultation" />
                            </span>
                            <span className={css.breakdownValue}>
                              <FormattedMessage id="ReservationDetailsPage.afterConsultationValue" values={{ formatted40 }} />
                            </span>
                          </div>
                        </div>

                        <div className={css.breakdownItem}>
                          <div className={css.breakdownContent}>
                            <span className={css.breakdownLabel}>
                              <FormattedMessage id="ReservationDetailsPage.daysBeforeJourney" />
                            </span>
                            <span className={css.breakdownValue}>
                              <FormattedMessage id="ReservationDetailsPage.daysBeforeJourneyValue" values={{ formatted30 }} />
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className={css.cardHeaderDivider} />

                    <div className={css.bookingTerms}>
                      <h4 className={css.bookingTermsTitle}>
                        <FormattedMessage id="ReservationDetailsPage.bookingTermsTitle" />
                      </h4>
                      <p className={css.bookingTermsText}>
                        <FormattedMessage id="ReservationDetailsPage.bookingTermsText" />
                      </p>
                    </div>
                  </>
                ) : null}
                {!isConversation && isDateBooked ? (
                  <div className={css.dateBookedError}>
                    <FormattedMessage id="ReservationDetailsPage.dateAlreadyBooked" />
                  </div>
                ) : null}

                {!isConversation && (checkingBookingCapacity || checkingGoogleCalendar) ? (
                  <div className={css.bookingCapacityStatus} role="status">
                    <FormattedMessage id="ReservationDetailsPage.checkingBookingCapacity" />
                  </div>
                ) : !isConversation && googleCalendarError ? (
                  <div className={css.bookingCapacityNotice} role="status">
                    {googleCalendarError}
                  </div>
                ) : !isConversation && bookingCapacity?.isAvailable === false ? (
                  <div className={css.bookingCapacityNotice} role="status">
                    <FormattedMessage
                      id="ReservationDetailsPage.bookingCapacityReached"
                      values={{ date: formattedSelectedDate }}
                    />
                  </div>
                ) : !isConversation && bookingCapacityError ? (
                  <div className={css.bookingCapacityNotice} role="status">
                    {bookingCapacityError}
                  </div>
                ) : null}
              </div>

              <PrimaryButton
                type="submit"
                className={isConversation ? css.conversationSubmitBtn : css.submitBtn}
                disabled={isSubmitDisabled}
              >
                {isConversation ? (
                  <>
                    <FormattedMessage id="ReservationDetailsPage.continue" />
                    <IconCard brand="rightArrow" />
                  </>
                ) : (
                  <>
                    <FormattedMessage id="ReservationDetailsPage.continueButton" />
                    <IconCard brand="rightArrow" />
                  </>
                )}
              </PrimaryButton>
            </Form>
          );
        }}
      />
    </div>
  );

  const renderReservationSummaryCard = () => (
    <div className={css.detailsCard}>
      <div className={css.cardContent}>
        <button type="button" className={css.backBtn} onClick={handleBack}>
          <FormattedMessage id="ReservationDetailsPage.backButton" />
        </button>
        <h3 className={css.yourDetailsHeading}>
          <FormattedMessage id="BookingFixedDurationForm.reservationDetails" />
        </h3>

        <div className={css.reservationSummary}>
          <div className={css.summaryItem}>
            <div className={css.summaryLabel}>
              <FormattedMessage id="BookingFixedDurationForm.experience" />
            </div>
            <div className={css.summaryValue}>{title}</div>
          </div>

          <div className={css.summaryItem}>
            <div className={css.summaryLabel}>
              <FormattedMessage id="BookingFixedDurationForm.guests" />
            </div>
            <div className={css.summaryValue}>
              {Number(publicData?.elopementEp) === 1 || Number(publicData?.elopementEp) === 2 ? (
                '30 Guests'
              ) : expType === 'multiple_day' && getTitleAfterHyphen(title) ? (
                getTitleAfterHyphen(title)
              ) : (
                <>
                  {bookingData?.numberOfGuests}{' '}
                  <FormattedMessage id="BookingFixedDurationForm.guestsText" />
                </>
              )}
            </div>
          </div>

          <div className={css.summaryItem}>
            <div className={css.summaryLabel}>
              <FormattedMessage id="BookingFixedDurationForm.date" />
            </div>
            <div className={css.summaryValue}>
              {(() => {
                const startTime = bookingData?.bookingStartTime;
                if (!startTime) {
                  return <FormattedMessage id="ReservationDetailsPage.dateNotSelected" />;
                }
                const dateObj =
                  startTime instanceof Date
                    ? startTime
                    : typeof startTime === 'number'
                      ? new Date(startTime)
                      : /^\d+$/.test(String(startTime))
                        ? new Date(Number.parseInt(startTime, 10))
                        : new Date(startTime);

                return !isNaN(dateObj.getTime()) ? (
                  intl.formatDate(dateObj, {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                    timeZone,
                  })
                ) : (
                  <FormattedMessage id="ReservationDetailsPage.dateNotSelected" />
                );
              })()}
            </div>
          </div>

          {(() => {
            const baseMoney = bookingData?.numberOfGuests
              ? getSelectedPrice(bookingData.numberOfGuests)
              : price;
            const discountInfo =
              bookingData?.discountCode && bookingData?.discountPercentage
                ? calculateDiscount(baseMoney, bookingData.discountPercentage)
                : bookingData?.discountCode
                ? calculateDiscount(baseMoney, 20)
                : null;

            return (
              <div className={css.summaryItem}>
                <div className={css.summaryLabel}>
                  <FormattedMessage id="BookingFixedDurationForm.invest" />
                </div>
                {discountInfo ? (
                  <div className={css.summaryInvestWithDiscount}>
                    <div className={css.investPricesRow}>
                      <span className={css.investOriginalPrice}>
                        {formatMoney(intl, discountInfo.originalMoney)}
                      </span>
                      <span className={css.investDiscountedPrice}>
                        {formatMoney(intl, discountInfo.discountedMoney)}
                      </span>
                    </div>
                    <div className={css.investDiscountBadge}>
                      <FormattedMessage
                        id="ReservationSummaryPage.discountInvestNotice"
                        defaultMessage="Discount ({code} applied: {percentage}% off)"
                        values={{
                          code: bookingData.discountCode,
                          percentage: discountInfo.discountPercentage,
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className={css.summaryValue}>
                    {formatMoney(intl, baseMoney)}
                  </div>
                )}
              </div>
            );
          })()}

          <div className={css.paymentNote}>
            <FormattedMessage id="BookingFixedDurationForm.paymentNote" />
          </div>
        </div>
      </div>

      <PrimaryButton
        type="button"
        onClick={() => handleProceedToCheckout()}
        className={css.submitBtn}
      >
        <FormattedMessage id="BookingFixedDurationForm.proceedToPayment" />
        <IconCard brand="rightArrow" />
      </PrimaryButton>
    </div>
  );

  const renderMainCard = () => {
    return renderReservationSummaryCard();
  };

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: schemaTitle,
      }}
    >
      <LayoutSingleColumn
        topbar={topbar}
        footer={<FooterContainer />}
        mainColumnClassName={css.fullWidthMain}
      >
        <div className={isConversation ? css.conversationRoot : css.root}>
          {isConversation ? (
            <>
              <div className={css.conversationHeroWrapper}>
                {/* <img
                  src={require('../../assets/listingbackgroundcss.jpg')}
                  alt="Reservation Details"
                  className={css.conversationHeroImage}
                /> */}
                <div className={css.conversationHeroOverlay} />
                <div className={css.conversationHeroContent}>
                  <div className={css.titleLogoline}>{title ? title.toUpperCase() : ''}</div>
                  <h1 className={css.conversationHeroTitle}>
                    <FormattedMessage id="ReservationDetailsPage.beginYourJourney" />
                  </h1>
                  <p className={css.conversationHeroSubtitle}>
                    <FormattedMessage id="ReservationDetailsPage.journeyDisclaimer" />
                  </p>
                </div>
              </div>

              <div ref={cardSectionRef} className={css.conversationCardSection}>
                {renderMainCard()}
                {showDetailsForm ? (
                  <ul className={css.paymentDisclaimer}>
                    <li>
                      <FormattedMessage id="ReservationDetailsPage.disclaimerPay" />
                    </li>
                    <li>
                      <FormattedMessage id="ReservationDetailsPage.disclaimerCustomizable" />
                    </li>
                    <li>
                      <FormattedMessage id="ReservationDetailsPage.disclaimerRefine" />
                    </li>
                    <li>
                      <FormattedMessage id="ReservationDetailsPage.disclaimerConfirmation" />
                    </li>
                  </ul>
                ) : null}
              </div>
            </>
          ) : (
            <>
              <div className={css.staticHeroWrapper}>
                <picture>
                  {/* Mobile: 9:16 portrait crop */}
                  <source
                    media="(max-width: 600px)"
                    srcSet={heroImageMobileSrcSet || heroImageMobileUrl}
                    sizes="100vw"
                    width="600"
                    height="1067"
                  />
                  {/* Tablet: 3:4 portrait crop */}
                  <source
                    media="(max-width: 1240px)"
                    srcSet={heroImageTabletSrcSet || heroImageTabletUrl}
                    sizes="100vw"
                    width="1240"
                    height="1365"
                  />
                  {/* Desktop fallback: 16:9 landscape */}
                  <img
                    src={optimizedHeroImageUrl}
                    srcSet={heroImageSrcSet}
                    sizes="100vw"
                    alt={title}
                    className={css.heroImage}
                    fetchPriority="high"
                    decoding="async"
                    width="1920"
                    height="1080"
                  />
                </picture>
                <div className={css.heroOverlay} />
                <div className={css.heroContent}>
                  {/* <div className={css.titleLogoline}>{heroLocationLine}</div> */}
                  <h1 className={css.heroTitle}>{title}</h1>
                  {listingSubtitle ? (
                    <>
                      <p className={css.heroSubtitle}>
                        {listingSubtitle.length > 70 && !isHeroSubtitleExpanded ? (
                          <>
                            <span className={css.heroSubtitleFull}>{listingSubtitle}</span>
                            <span className={css.heroSubtitleTruncated}>
                              {truncateAtWord(listingSubtitle, 70)}
                            </span>
                          </>
                        ) : (
                          listingSubtitle
                        )}
                      </p>
                      {listingSubtitle.length > 70 ? (
                        <button
                          type="button"
                          className={css.heroReadMore}
                          onClick={() => setIsHeroSubtitleExpanded(prev => !prev)}
                        >
                          <FormattedMessage
                            id={
                              isHeroSubtitleExpanded
                                ? 'ReservationDetailsPage.readLess'
                                : 'ReservationDetailsPage.readMore'
                            }
                          />
                          <IconCard brand="rightArrow" />
                        </button>
                      ) : null}
                    </>
                  ) : null}
                </div>
              </div>

              <div ref={cardSectionRef} className={css.cardSection}>{renderMainCard()}</div>
            </>
          )}
        </div>
      </LayoutSingleColumn>
      <BookingLoginModal
        isOpen={loginModalState.isOpen}
        email={loginModalState.email}
        onClose={() =>
          setLoginModalState(previous => ({
            ...previous,
            isOpen: false,
            recoveryEmailSent: false,
          }))
        }
        onSubmit={handleBookingLogin}
        onForgotPassword={handleForgotPassword}
        onManageDisableScrolling={onManageDisableScrolling}
        loginInProgress={loginInProgress}
        loginError={loginError}
        recoveryInProgress={recoveryInProgress}
        recoveryError={recoveryError}
        recoveryEmailSent={loginModalState.recoveryEmailSent}
      />
    </Page>
  );
};

const mapStateToProps = state => {
  const {
    showListingError,
    monthlyTimeSlots,
    timeSlotsForDate,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
  } = state.ListingPage;
  const { currentUser } = state.user;
  const { loginError } = state.auth;
  const { recoveryInProgress, recoveryError } = state.PasswordRecoveryPage;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    showListingError,
    monthlyTimeSlots,
    timeSlotsForDate,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    loginInProgress: authenticationInProgress(state, 'loginInProgress'),
    loginError,
    recoveryInProgress,
    recoveryError,
    bookingFlowData: state.user?.bookingFlowData || null,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onFetchTimeSlots: (listingId, start, end, timeZone, options) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone, options)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)),
  callSetInitialValues: (setInitialValuesFunc, values, saveToSessionStorage) =>
    dispatch(setInitialValuesFunc(values, saveToSessionStorage)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onUpdateProfile: data => dispatch(updateProfile(data)),
  onSignup: params => dispatch(signup(params)),
  onLogin: (email, password) => dispatch(login(email, password)),
  onRecoverPassword: email => dispatch(recoverPassword({ email })).unwrap(),
  onFetchCurrentUser: options => dispatch(fetchCurrentUser(options)),
});

const ReservationSummaryPage = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(ReservationDetailsPageComponent);

export default ReservationSummaryPage;
